import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";

import { Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { leaderboardAction } from "redux/slice/leaderboard";
import { enqueueSnackbar } from "notistack";

import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import useDebouncedValue from "hooks/useDebouncedValue";

import { FILTER_PARAMS, DATE_FILTER_OPTIONS, FETCH_KPI_ACTIVITIES } from "lib/constants";
import { getAPICall, postAPICall } from "lib/callApi";

import { getFilterDates } from "utils/HelperFunctions";
import Loader from "utils/Loader";

import ListingTable from "./ListingTable";
import TableFilterModal from "./TableFilterModal";
import FilterModal from "./FilterModal";

const otherTableFilterOptions = [
  { id: "totalKpis", name: "Completed KPIs" },
  { id: "totalPoints", name: "Achieved Points" },
  { id: "quotaAttainment", name: "Quota Attainment" },
  { id: "totalTenPointDays", name: "Total 10 Point Days" },
  { id: "activeStreaks", name: "Active Point Goal Streak" },
  { id: "allTimeStreaks", name: "All Time Point Goal Streak" },
  // { id: "avgTenPointDays", name: "Average 10 Point Days" },
  // { id: "avgPoints", name: "Average Points" },
];

const ContestLeaderBoard = (props) => {
  const {
    history,
    filters,
    setFilters,
    columns,
    setColumns,
    initialLoading,
    setInitialLoading,
    lastRefreshedOn,
    tableFilterOptions,
    setTableFilterOptions,
    tableFilterOptionsLoading,
    setTableFilterOptionsLoading,
  } = props;

  const locationSearch = new URLSearchParams(window.location.search); // to get query params from the url
  const urlQueryParams = {
    // filters
    location: locationSearch?.getAll("location") || [],
    team: locationSearch?.getAll("team") || [],
    user: locationSearch?.getAll("user") || [],
    role: locationSearch?.getAll("role") || [],
    reportsTo: locationSearch?.getAll("reportsTo") || [],
    dateFilter: locationSearch?.get("dateFilter") || "",
    startDate: locationSearch?.get("startDate") || "",
    endDate: locationSearch?.get("endDate") || "",

    // Pagination
    order: locationSearch?.get("order") || "asc",
    orderBy: locationSearch?.get("orderBy") || "rank",
    pageSize: locationSearch?.get("pageSize") || 10,
    pageNumber: locationSearch?.get("pageNumber") || 0,

    // columns
    other: locationSearch?.getAll("other") || ["totalKpis", "totalPoints"],
    kpis: locationSearch?.getAll("kpis") || [],

    // search
    search: locationSearch?.get("search") || "",
  }; // url query object based on url query params

  // States
  const [isFilterEdited, setIsFilterEdited] = useState(false); // to identify the filters are edited or not
  const [createNewView, setCreateNewView] = useState(false); // to set filters first time loading the page
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false); // to handle filter model

  const [options, setOptions] = useState({}); // filters dropdown options
  const [isTableFilterModalOpen, setIsTableFilterModalOpen] = useState(false); // to handle table filter model
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [modalFilters, setModalFilters] = useState();
  const [filterComponents, setFilterComponents] = useState([]);
  const [dateRange, setDateRange] = useState({});

  const [pagination, setPagination] = useState({
    order: "asc",
    orderBy: "rank",
    pageSize: 10,
    pageNumber: 0,
  });
  const [modalColumns, setModalColumns] = useState({
    other: ["totalKpis", "totalPoints"],
    kpis: [],
  });

  // columnsWithNames is used to render selected columns in the UI which contains selected columns names
  const columnsWithNames = useMemo(
    () => ({
      other: columns?.other?.length
        ? columns.other
            .map((column) => {
              const matchedObject = tableFilterOptions?.other?.find(
                (filterOption) => filterOption.id === column
              );
              if (!matchedObject) return null;
              return {
                id: matchedObject.id,
                name: matchedObject.name,
              };
            })
            .filter((kpi) => !!kpi)
        : [],
      kpis: columns?.kpis?.length
        ? columns.kpis
            .map((column) => {
              const matchedObject = tableFilterOptions?.kpis?.find(
                (filterOption) => filterOption.id === column
              );
              if (!matchedObject) return null;
              return {
                id: matchedObject.id,
                name: matchedObject.name,
              };
            })
            .filter((kpi) => !!kpi)
        : [],
    }),
    [columns, tableFilterOptions]
  );

  const [userSearchTerm, setUserSearchTerm] = useState(""); // to store leaderboard search box value
  const userSearchTermDebouncedValue = useDebouncedValue(userSearchTerm, 500); // to reduce leaderboard api call on each action of search box

  // Redux
  const dispatch = useDispatch();
  const loginUserState = useSelector((state) => state.loginUser.loginUserState);
  const loading = useSelector((state) => state.leaderboard.loading);

  /**
   * handleFilterModalOpen: use to open filter modal
   */
  const handleFilterModalOpen = () => {
    setModalFilters({ ...filters });
    setIsFilterModalOpen(true);
  };

  /**
   * handleTableFilterModalOpen: use to open table filter modal
   */
  const handleTableFilterModalOpen = () => {
    setModalColumns({ ...columns });
    setIsTableFilterModalOpen(true);
  };

  /**
   * fetchLeaderboardData: Use to fetch leaderboard data
   * @param {*} optionObj the filter object based on which the leaderboard list was fetched
   * @returns
   */
  const fetchLeaderboardData = (optionObj) => {
    if (initialLoading || createNewView || isFilterModalOpen || isTableFilterModalOpen) return;
    setInitialLoading(true);

    if (!["rank", "name"].includes(pagination.orderBy)) {
      if (pagination.orderBy.startsWith("kpi-")) {
        const kpiId = pagination.orderBy.split("-")[1];
        if (!columns.kpis.includes(Number(kpiId)))
          setPagination({ ...pagination, orderBy: "rank" });
      } else if (!columns.other.includes(pagination.orderBy)) {
        setPagination({ ...pagination, orderBy: "rank" });
      }
    }

    if (optionObj.dateFilter === "Custom" && (!optionObj.startDate || !optionObj.endDate)) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Select date interval!",
        }),
        {
          variant: "custom",
        }
      );
      return;
    }

    const payloadBody = {
      filters: {
        dateType:
          optionObj.dateFilter?.toLowerCase().indexOf("quarter") < 0 &&
          optionObj.dateFilter?.toLowerCase().indexOf("year") < 0
            ? 1
            : 2,
        dateFilter: optionObj.dateFilter,
        startDate: optionObj.startDate,
        endDate: optionObj.endDate,
        timezone: loginUserState.timezone,
        locationList: optionObj?.location || [],
        teamList: optionObj?.team || [],
        roleList: optionObj?.role || [],
        userList: optionObj?.user || [],
        reportsToList: optionObj?.reportsTo || [],
        search: userSearchTermDebouncedValue,
      },
      columns,
      pagination,
    };

    if (optionObj.dateFilter !== "Custom") {
      const dates = getFilterDates(
        optionObj.dateFilter,
        loginUserState.timezone,
        loginUserState.fiscalYearStart
      );

      payloadBody.filters.startDate = dates.startDate;
      payloadBody.filters.endDate = dates.endDate;
    }

    setDateRange({
      startDate: payloadBody.filters.startDate,
      endDate: payloadBody.filters.endDate,
    });

    dispatch(leaderboardAction.getLeaderboardDataRequest(payloadBody));
    setInitialLoading(false);
  };

  /**
   * Use to modify the browser url query params
   * @param {*} finalQueryStringObject: object to convert into query params
   */
  const updateQueryStringOfURL = (finalQueryStringObject) => {
    const newQueryString =
      Object.keys(finalQueryStringObject)
        ?.reduce((finalQueryString, key) => {
          if (["string", "number"].includes(typeof finalQueryStringObject[key])) {
            finalQueryString += `&${key}=${finalQueryStringObject[key]}`;
          } else if (
            Array.isArray(finalQueryStringObject[key]) &&
            finalQueryStringObject[key].length
          ) {
            finalQueryStringObject[key].forEach((l) => {
              finalQueryString += `&${key}=${l.label || l}`;
            });
          }
          return finalQueryString;
        }, "")
        ?.slice(1) || "";
    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${newQueryString}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  };

  /**
   * syncOptionsAndFilter: Based on provided filters in props, fetch drop down options values and set filters state values
   * @param {*} _filters Use to fetch drop list options for all filters and use to fetch leaderboard list
   * @param {*} changeURL Use to update current browser URL. ( To update query params in the url to make it sharable )
   * @param {*} fromModal Use to skip updating main filters state ( To skip leaderboard api call ) and just update the filter modal values
   */
  const syncOptionsAndFilter = async (
    _filters,
    changeURL = true,
    fromModal = false,
    populateDefault = false
  ) => {
    try {
      if (fromModal) setIsModalLoading(true);

      const response = await postAPICall(FILTER_PARAMS, true, {
        filters: {
          filterLocations: _filters?.location?.map((location) => location.id) || [],
          filterTeams: _filters?.team?.map((team) => team.id) || [],
          filterRoles: _filters?.role?.map((role) => role.id) || [],
          filterUsers: _filters?.user?.map((user) => user.userCompanyLinkId) || [],
          filterReportsTo: _filters?.reportsTo?.map((reportTo) => reportTo.id) || [],
        },
      });

      if (response?.data?.success) {
        const {
          users: user,
          reportsTo,
          roles: role,
          teams: team,
          locations: teamLocation,
        } = response.data.data;

        const optionObj = {
          team,
          location: teamLocation,
          user,
          role,
          reportsTo,
          dateFilter: DATE_FILTER_OPTIONS,
          startDate: "",
          endDate: "",
        };

        const updatedFilter = {};

        Object.keys(optionObj).forEach((key) => {
          if (Array.isArray(optionObj[key]) && Array.isArray(_filters[key])) {
            const arr1 = optionObj[key].map((obj) => obj.id);
            const arr2 = _filters[key];
            updatedFilter[key] = arr2.filter((arr2Obj) => arr1.includes(arr2Obj.id));
          }
        });

        if (DATE_FILTER_OPTIONS.includes(_filters.dateFilter)) {
          updatedFilter.dateFilter = _filters.dateFilter;

          if (_filters.dateFilter === "Custom") {
            updatedFilter.startDate = _filters.startDate;
            updatedFilter.endDate = _filters.endDate;
          }
        }

        if (populateDefault) {
          updatedFilter.team = optionObj.team;
          updatedFilter.dateFilter = "This Week";
        }

        if (history.pushState && changeURL) {
          const finalQueryStringObject = {
            ...pagination,
            ...columns,
            ...updatedFilter,
          };
          updateQueryStringOfURL(finalQueryStringObject);
        }

        if (fromModal) {
          setModalFilters({ ...updatedFilter });
        } else {
          setPagination({ ...pagination, pageNumber: 0 });

          if (initialLoading && changeURL) setInitialLoading(false);
          setFilters({ ...updatedFilter });
        }

        setOptions(optionObj);
      } else if (response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              response?.response?.data?.message || response?.message || "Could not retrieve data!",
          }),
          {
            variant: "custom",
          }
        );
      }
      if (fromModal) setIsModalLoading(false);
    } catch (error) {
      if (fromModal) setIsModalLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not retrieve data!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  /**
   * fetchTableFilterOptions: use to fetch companies all kpi list
   */
  const fetchTableFilterOptions = async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;
    setTableFilterOptionsLoading(true);
    try {
      const companyKpis = await getAPICall(FETCH_KPI_ACTIVITIES, true, {
        filter: {
          fetchOnlyActiveKpis: "true",
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          timezone: loginUserState.timezone,
          selectedUsers: urlQueryParams?.user || [],
        },
      });

      if (companyKpis?.data?.success) {
        setTableFilterOptions({ kpis: companyKpis?.data?.data, other: otherTableFilterOptions });
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not retrieve data!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setTableFilterOptionsLoading(false);
    }
  };

  /**
   * changeURL: To modify current URL filters based on new provided filters
   * @param {*} _filters new filters which needs to update the current URL
   */
  const changeURL = (_filters, _pagination, _columns, _search) => {
    const updatedFilter = {};

    if (_filters) {
      const optionObj = {
        team: options.team,
        location: options.location,
        user: options.user,
        role: options.role,
        reportsTo: options.reportsTo,
        dateFilter: DATE_FILTER_OPTIONS,
        startDate: "",
        endDate: "",
      };

      Object.keys(optionObj).forEach((key) => {
        if (Array.isArray(optionObj[key]) && Array.isArray(_filters[key])) {
          const arr1 = optionObj[key].map((obj) => obj.id);
          const arr2 = _filters[key];
          updatedFilter[key] = arr2.filter((arr2Obj) => arr1.includes(arr2Obj.id));
        }
      });

      if (DATE_FILTER_OPTIONS.includes(_filters.dateFilter)) {
        updatedFilter.dateFilter = _filters.dateFilter;

        if (_filters.dateFilter === "Custom") {
          updatedFilter.startDate = _filters.startDate;
          updatedFilter.endDate = _filters.endDate;
        }
      }
    }

    const updatedPagination = {
      ...(_pagination || pagination),
      ...(_filters || _search ? { pageNumber: 0 } : {}),
    };

    setPagination({ ...updatedPagination });
    if (_columns) setColumns({ ..._columns });
    if (_filters) setFilters({ ...updatedFilter });

    const finalQueryStringObject = {
      ...updatedPagination,
      ...(_columns || columns),
      ...(_filters ? updatedFilter : filters),
      ...(_search || userSearchTermDebouncedValue
        ? { search: _search || userSearchTermDebouncedValue }
        : {}),
    };

    if (history.pushState) {
      updateQueryStringOfURL(finalQueryStringObject);
    }
  };

  /**
   * changeURLBasedOnFilter: To modify current URL and refetch leaderboard list and set filter updated state
   * @param {*} _filters new filters which needs to update the current URL, refetch leader board list
   */
  const changeURLBasedOnFilter = (_filters) => {
    changeURL(_filters);
    fetchLeaderboardData(_filters);
    setIsFilterEdited(true);
    if (createNewView) setCreateNewView(false);
  };

  /**
   * fetchParamsAndSetFilterFromURL: use to fetch filters drop down options and set filters using URL query params
   */
  const fetchParamsAndSetFilterFromURL = async () => {
    try {
      const response = await postAPICall(FILTER_PARAMS, true, {
        filters: {
          filterLocations: [],
          filterTeams: [],
          filterRoles: [],
          filterUsers: [],
          filterReportsTo: [],
        },
      });

      if (response?.data?.success) {
        const user = response.data.data.users;
        const { reportsTo } = response.data.data;
        const role = response.data.data.roles;
        const team = response.data.data.teams;
        const teamLocation = response.data.data.locations;

        setUserSearchTerm(urlQueryParams?.search || "");

        // For filters
        const optionObj = {
          location: teamLocation,
          team,
          user,
          role,
          reportsTo,
          dateFilter: DATE_FILTER_OPTIONS,
          startDate: "",
          endDate: "",
        };

        const opObj = {};

        Object.keys(optionObj).forEach((key) => {
          if (Array.isArray(urlQueryParams[key])) {
            opObj[key] = urlQueryParams[key]
              .map((fl) => optionObj[key].find((ol) => ol.label === fl))
              .filter((o) => o);
          } else if (urlQueryParams[key] === "dateFilter") {
            if (DATE_FILTER_OPTIONS.includes(urlQueryParams[key])) opObj[key] = urlQueryParams[key];
          } else {
            opObj[key] = urlQueryParams[key];
          }
        });

        // For Pagination
        const paginationQueryParams = {
          order: "asc",
          orderBy: "rank",
          pageSize: 10,
          pageNumber: 0,
        };

        const updatedPagination = {};
        Object.keys(paginationQueryParams).forEach((key) => {
          updatedPagination[key] =
            ["pageSize", "pageNumber"].includes(key) && urlQueryParams[key]
              ? parseInt(urlQueryParams[key], 10)
              : urlQueryParams[key];
        });

        // For columns
        const columnsQueryParams = {
          other: [],
          kpis: [],
        };

        const updatedColumns = {
          other: [],
          kpis: [],
        };

        Object.keys(columnsQueryParams).forEach((key) => {
          updatedColumns[key] =
            (key === "kpis"
              ? urlQueryParams[key]?.map((kpi) => parseInt(kpi, 10))
              : urlQueryParams[key]) || [];
        });

        setColumns({ ...updatedColumns });
        setPagination({ ...updatedPagination });
        setInitialLoading(false);
        setFilters({ ...filters, ...opObj });
      } else if (response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              response?.response?.data?.message || response.message || "Could not retrieve data!",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not retrieve data!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  // to  process url params on initial render
  /**
   * handleInitialPageURL: Use to set filters on initial component load
   */
  const handleInitialPageURL = async () => {
    setInitialLoading(true); // to skip initial leaderboard api calling with before url params processed

    let isURLEmpty = true; // Initially consider that the url filter are empty

    // check if any filters are set in URl and set the isURLEmpty flag
    Object.keys(urlQueryParams).forEach((key) => {
      if (
        isURLEmpty &&
        ((Array.isArray(urlQueryParams[key]) && urlQueryParams[key].length) ||
          (typeof urlQueryParams[key] === "string" && urlQueryParams[key]))
      )
        isURLEmpty = false;
    });

    // if not filters are included in the query params and filters are not edited yet then use user's saved default filters
    if ((isURLEmpty || !locationSearch?.size) && !isFilterEdited) {
      if (loginUserState?.defaultLeaderboardFilter) {
        const defaultFilter = JSON.parse(loginUserState?.defaultLeaderboardFilter || "{}");

        if (defaultFilter?.kpis || defaultFilter?.other) {
          setColumns({
            other: defaultFilter?.other || [],
            kpis: defaultFilter?.kpis || [],
          });
        }

        await syncOptionsAndFilter({ ...filters, ...defaultFilter });
      } else {
        // setCreateNewView(true);
        await syncOptionsAndFilter(filters, true, false, true);
      }
    } else {
      // Or else use filters from query params
      await fetchParamsAndSetFilterFromURL();
    }
    setInitialLoading(false);
  };

  useLayoutEffect(() => {
    handleInitialPageURL(); // on initial render check url query params
  }, []);

  useEffect(() => {
    fetchLeaderboardData(filters);
  }, [filters, pagination, lastRefreshedOn]);

  useEffect(() => {
    if (!initialLoading) fetchTableFilterOptions();
  }, [dateRange, initialLoading]);

  useEffect(() => {
    if (!initialLoading) changeURL(null, null, null, userSearchTermDebouncedValue);
  }, [userSearchTermDebouncedValue]);

  useEffect(() => {
    const components = [];
    Object.keys(filters).forEach((key) => {
      if (Array.isArray(filters[key]) && filters[key].length > 0) {
        components.push(
          <React.Fragment key={key}>
            <MDTypography
              variant="body1"
              sx={{ textTransform: "capitalize", mr: 1, color: "#4c4c4c", fontSize: "1rem" }}
            >
              {`${key === "reportsTo" ? "Reports To " : key}: `}
            </MDTypography>
            <MDTypography
              variant="body1"
              sx={{ fontStyle: "italic", color: "#7882a6", fontSize: ".9rem", mr: 2 }}
            >
              {Array.isArray(filters[key]) &&
                filters[key]?.map(
                  (appliedFilter, inx) =>
                    `${appliedFilter.label} ${inx !== filters[key].length - 1 ? "," : ""} `
                )}
            </MDTypography>
          </React.Fragment>
        );
      } else if (key === "dateFilter" && filters[key]) {
        components.push(
          <React.Fragment key={key}>
            <MDTypography
              variant="body1"
              sx={{
                textTransform: "capitalize",
                mr: 1,
                color: "#4c4c4c",
                fontSize: "1rem",
                paddingLeft: "50px",
              }}
            >
              Date Filter
            </MDTypography>
            <MDTypography
              variant="body1"
              sx={{ fontStyle: "italic", color: "#7882a6", fontSize: ".9rem", mr: 2 }}
            >
              {filters[key]}
            </MDTypography>
          </React.Fragment>
        );
      }
    });

    setFilterComponents(components);
  }, [filters]);

  return (
    <>
      <Loader loading={loading || tableFilterOptionsLoading} />
      {createNewView ? (
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <MDTypography>Build your custom view</MDTypography>
          <MDButton color="primaryRed" onClick={handleFilterModalOpen}>
            Create custom view
          </MDButton>
        </MDBox>
      ) : (
        <>
          <MDBox>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              spacing={2}
              paddingTop={4}
            >
              <Grid
                item
                xs={8}
                md={10}
                display="flex"
                alignItems="center"
                sx={{ flexWrap: "wrap" }}
              >
                <MDInput
                  value={userSearchTerm}
                  onChange={(event) => setUserSearchTerm(event.target.value)}
                  label="Search employee"
                  sx={{
                    mr: 2,
                    width: "30%",
                    "& .MuiInputBase-root": {
                      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                      border: "1px solid rgba(0, 0, 0, 0.15)",
                    },
                  }}
                />
                <MDBox
                  sx={{
                    textTransform: "capitalize",
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    paddingLeft: "50px",
                  }}
                  display="inline-flex"
                  ml={1}
                >
                  {filterComponents}
                </MDBox>
              </Grid>
              <Grid item xs={4} md={2}>
                <MDButton
                  sx={{ minWidth: "100%", borderRadius: "2rem" }}
                  color="primaryRed"
                  onClick={handleFilterModalOpen}
                >
                  Filter
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox>
            <ListingTable
              dateRange={dateRange}
              pagination={pagination}
              changeURL={changeURL}
              handleTableFilterModalOpen={handleTableFilterModalOpen}
              columnsWithNames={columnsWithNames}
            />

            {isTableFilterModalOpen && (
              <TableFilterModal
                open={isTableFilterModalOpen}
                handleClose={() => setIsTableFilterModalOpen((prev) => !prev)}
                options={tableFilterOptions}
                modalColumns={modalColumns}
                setModalColumns={setModalColumns}
                changeURL={changeURL}
              />
            )}
          </MDBox>
        </>
      )}
      {isFilterModalOpen && (
        <FilterModal
          open={isFilterModalOpen}
          handleClose={() => setIsFilterModalOpen((prev) => !prev)}
          filters={modalFilters}
          changeURLBasedOnFilter={changeURLBasedOnFilter}
          options={options}
          setModalFilters={setModalFilters}
          syncOptionsAndFilter={syncOptionsAndFilter}
          isModalLoading={isModalLoading}
          columns={columns}
        />
      )}
    </>
  );
};

export default ContestLeaderBoard;
